import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Building2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetOrganization } from '@/api/organizations/get-organization';
import { ProjectsTab } from '@/components/organization/projects-tab';
import { MembersTab } from '@/components/organization/members-tab';
import { InvitationsTab } from '@/components/organization/invitations-tab';
import { SettingsTab } from '@/components/organization/settings-tab';
import { InviteMemberDialog } from '@/components/organization/invite-member-dialog';
import { formatDate } from '@/utils/date';
import { canManageInvitations, canManageMembers, canUpdateOrganization, canViewMembers } from '@/utils/permissions';

export const Route = createFileRoute('/_authenticated/organizations/$id')({
  component: OrganizationPage
});

function OrganizationPage() {
  const navigate = useNavigate();
  const { id: organizationId } = Route.useParams();
  const organizationQuery = useGetOrganization(organizationId);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');

  const organization = organizationQuery.data;
  const userRole = organization?.currentUserRole;

  const permissions = useMemo(
    () => ({
      canViewMembers: userRole ? canViewMembers(userRole) : false,
      canManageMembersTab: userRole ? canManageMembers(userRole) : false,
      canManageInvitationsTab: userRole ? canManageInvitations(userRole) : false,
      canManageSettings: userRole ? canUpdateOrganization(userRole) : false
    }),
    [userRole]
  );

  const availableTabs = useMemo(() => {
    const tabs = ['projects'];
    if (permissions.canViewMembers) tabs.push('members');
    if (permissions.canManageInvitationsTab) tabs.push('invitations');
    if (permissions.canManageSettings) tabs.push('settings');
    return tabs;
  }, [permissions]);

  const validActiveTab = useMemo(() => {
    return availableTabs.includes(activeTab) ? activeTab : 'projects';
  }, [availableTabs, activeTab]);

  const handleDeleted = async () => {
    await navigate({ to: '/ideas' });
  };

  if (organizationQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-6xl space-y-6 text-muted-foreground">Chargement...</div>
        </div>
      </div>
    );
  }

  if (organizationQuery.isError || !organization) {
    const status = (organizationQuery.error as { status?: number }).status;
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-6xl space-y-6 text-center">
            <p className="text-muted-foreground">
              {status === 401 || status === 403 ? 'Accès refusé' : 'Organisation introuvable.'}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate({ to: '/ideas' })}>
                Retour
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-6xl space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/ideas' })}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="h-8 w-8 text-primary" />
                <h1 className="text-3xl md:text-4xl font-bold">{organization.name}</h1>
              </div>
              {organization.description && <p className="text-muted-foreground">{organization.description}</p>}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                <Calendar className="h-4 w-4" />
                Créée le {formatDate(organization.createdOn)}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs key={organizationId} value={validActiveTab} onValueChange={setActiveTab} className="w-full">
            <TabsList>
              <TabsTrigger value="projects">Projets</TabsTrigger>
              {permissions.canViewMembers && <TabsTrigger value="members">Membres</TabsTrigger>}
              {permissions.canManageInvitationsTab && <TabsTrigger value="invitations">Invitations</TabsTrigger>}
              {permissions.canManageSettings && <TabsTrigger value="settings">Paramètres</TabsTrigger>}
            </TabsList>

            <TabsContent value="projects" className="space-y-4">
              <ProjectsTab />
            </TabsContent>

            {permissions.canViewMembers && userRole && (
              <TabsContent value="members" className="space-y-4">
                <MembersTab
                  organizationId={organizationId}
                  userRole={userRole}
                  onInviteClick={() => setIsInviteDialogOpen(true)}
                />
              </TabsContent>
            )}

            {permissions.canManageInvitationsTab && (
              <TabsContent value="invitations" className="space-y-4">
                <InvitationsTab organizationId={organizationId} onInviteClick={() => setIsInviteDialogOpen(true)} />
              </TabsContent>
            )}

            {permissions.canManageSettings && (
              <TabsContent value="settings" className="space-y-4">
                <SettingsTab organization={organization} onDeleted={handleDeleted} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      <InviteMemberDialog
        organizationId={organizationId}
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInvitationCreated={() => {
          // Optionally refresh invitations list
        }}
      />
    </div>
  );
}
